import api from './api';
import { AgentProviderConfig, AgentSettings } from '../types';

const authHeaders = () => {
  const token = localStorage.getItem('ma_jwt');
  return token ? { Authorization: `Bearer ${token}`, 'X-Access-Token': token } : {};
};

const API_BASE = import.meta.env.VITE_API_URL || '/meos/api';

const isMaskedKey = (value: string) => {
  const trimmed = value.trim();
  return !trimmed || trimmed === '***' || trimmed.includes('*');
};

const sanitizeProviderForTest = (provider: AgentProviderConfig) => {
  const apiKey = typeof provider?.apiKey === 'string' ? provider.apiKey.trim() : '';
  const baseUrl = typeof provider?.baseUrl === 'string' ? provider.baseUrl.trim() : '';
  const model = typeof provider?.model === 'string' ? provider.model.trim() : '';

  return {
    type: provider?.type || 'GEMINI',
    ...(apiKey && !isMaskedKey(apiKey) ? { apiKey } : {}),
    ...(baseUrl ? { baseUrl } : {}),
    ...(model ? { model } : {}),
  };
};

export const agentService = {
  getSettings: async (): Promise<AgentSettings> => {
    try {
      const response = await api.get('/agent/settings');
      const data = response.data?.data || response.data;
      const { INITIAL_SETTINGS } = await import('../constants');
      return {
        ...INITIAL_SETTINGS.agent,
        ...data,
        provider: { ...INITIAL_SETTINGS.agent.provider, ...(data?.provider || {}) },
        providerConnections: Array.isArray(data?.providerConnections) && data.providerConnections.length > 0
          ? data.providerConnections.map((connection: any, index: number) => ({
              ...INITIAL_SETTINGS.agent.providerConnections?.[0],
              id: connection?.id || `connection-${index + 1}`,
              label: connection?.label || `Provider ${index + 1}`,
              ...connection,
            }))
          : INITIAL_SETTINGS.agent.providerConnections,
        taskRouting: {
          ...INITIAL_SETTINGS.agent.taskRouting,
          ...(data?.taskRouting || {}),
        },
        usage: {
          connections: data?.usage?.connections || INITIAL_SETTINGS.agent.usage?.connections || {},
        },
        modules: { ...INITIAL_SETTINGS.agent.modules, ...(data?.modules || {}) },
        rolesByModule: data?.rolesByModule || INITIAL_SETTINGS.agent.rolesByModule,
        enforcement: { ...INITIAL_SETTINGS.agent.enforcement, ...(data?.enforcement || {}) },
        enforcementByModule: data?.enforcementByModule || INITIAL_SETTINGS.agent.enforcementByModule,
        ruleToggles: data?.ruleToggles || INITIAL_SETTINGS.agent.ruleToggles,
        customRulesByModule: data?.customRulesByModule || INITIAL_SETTINGS.agent.customRulesByModule,
        knowledgeBaseNotesByModule: data?.knowledgeBaseNotesByModule || INITIAL_SETTINGS.agent.knowledgeBaseNotesByModule,
        guidance: { ...INITIAL_SETTINGS.agent.guidance, ...(data?.guidance || {}) },
        communications: {
          whatsapp: {
            ...INITIAL_SETTINGS.agent.communications.whatsapp,
            ...(data?.communications?.whatsapp || {}),
            draftGuardian: {
              ...INITIAL_SETTINGS.agent.communications.whatsapp.draftGuardian,
              ...(data?.communications?.whatsapp?.draftGuardian || {}),
            },
            contextAssistant: {
              ...INITIAL_SETTINGS.agent.communications.whatsapp.contextAssistant,
              ...(data?.communications?.whatsapp?.contextAssistant || {}),
            },
            intelligence: {
              ...INITIAL_SETTINGS.agent.communications.whatsapp.intelligence,
              ...(data?.communications?.whatsapp?.intelligence || {}),
            },
            replyWatcher: {
              ...INITIAL_SETTINGS.agent.communications.whatsapp.replyWatcher,
              ...(data?.communications?.whatsapp?.replyWatcher || {}),
            },
          },
          email: {
            ...INITIAL_SETTINGS.agent.communications.email,
            ...(data?.communications?.email || {}),
            draftGuardian: {
              ...INITIAL_SETTINGS.agent.communications.email.draftGuardian,
              ...(data?.communications?.email?.draftGuardian || {}),
            },
            contextAssistant: {
              ...INITIAL_SETTINGS.agent.communications.email.contextAssistant,
              ...(data?.communications?.email?.contextAssistant || {}),
            },
            intelligence: {
              ...INITIAL_SETTINGS.agent.communications.email.intelligence,
              ...(data?.communications?.email?.intelligence || {}),
            },
            replyWatcher: {
              ...INITIAL_SETTINGS.agent.communications.email.replyWatcher,
              ...(data?.communications?.email?.replyWatcher || {}),
            },
          },
        },
      };
    } catch (error: any) {
      console.error('[AgentService] Failed to fetch agent settings:', error?.message || error);
      const { INITIAL_SETTINGS } = await import('../constants');
      return INITIAL_SETTINGS.agent;
    }
  },
  logAudit: async (action: string, payload: Record<string, any>) => {
    try {
      await api.post('/agent/audit', { action, payload }, { headers: authHeaders() });
    } catch (error: any) {
      console.error('[AgentService] Failed to log agent audit:', error?.message || error);
    }
  },
  testProvider: async (provider: AgentSettings['provider'], module?: 'QUOTER' | 'SCHEDULED' | 'PLANNER' | 'FUEL' | 'WHATSAPP' | 'EMAIL') => {
    const token = localStorage.getItem('ma_jwt') || '';
    const sanitizedProvider = sanitizeProviderForTest(provider);
    const response = await fetch(`${API_BASE}/agent/test-provider${token ? `?token=${encodeURIComponent(token)}` : ''}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      credentials: 'include',
      body: JSON.stringify({
        provider: sanitizedProvider,
        module,
      }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw {
        response: {
          data,
        },
        message: data?.error || 'Provider test failed',
      };
    }
    return data?.data || data;
  },
};
